<template>
  <CCard class="chart-card chart-card--blue">
    <CCardHeader class="border-bottom-0 py-2">
      <div class="d-flex align-items-center">
        <h5 class="my-2">{{ getIndustryConfig.label }}</h5>
        <div class="ml-2">
          <template>
            ({{ industry.order_count | number }})
          </template>
        </div>
      </div>
      <div class="card-header-actions">
        <div class="d-flex align-items-center">
          <div class="text-primary d-md-down-none">
            <CDropdown
                placement="bottom-end"
                :caret="false"
                :offset="[0,0]"
            >
              <template #toggler-content>
                <div class="text-primary">
                  Currency
                  <CIcon name="cipInfo" size="lg"/>
                </div>
              </template>
              <div class="p-2">
                <CListGroup flush class="list-striped" v-if="industry.total_amount_per_currency"
                            style="min-width: 300px">
                  <CListGroupItem v-for="(amount, index) in industry.total_amount_per_currency" :key="index"
                                  class="flex-column align-items-start border-bottom-0">
                    <div class="d-flex w-100 justify-content-between">
                      <span>({{ amount.order_count | number }})</span>
                      <span class="font-weight-semi-bold">{{ amount.amount | currency(amount.currency) }}</span>
                    </div>
                  </CListGroupItem>
                </CListGroup>
                <div v-else class="text-center font-weight-semi-bold">
                  <translate>No data</translate>
                </div>
              </div>
            </CDropdown>
          </div>

          <h6 class="dot dot-blue font-weight-bold m-0" style="font-size: 15px">
            {{ industry.total_amount_eur | currency(currencyCodes.EUR) }}
          </h6>

          <h6 class="dot dot-green font-weight-bold m-0 ml-3" style="font-size: 15px">
            {{ industry.turnover | currency(currencyCodes.EUR) }}
          </h6>

          <h6 class="dot dot-yellow font-weight-bold m-0 ml-3" style="font-size: 15px">
            {{ industry.presale | currency(currencyCodes.EUR) }}
          </h6>

          <CButton :class="['btn-grid-toggle', 'pr-0', {'on': isExpanded && !isLoadingPhotoServices, 'loading': isLoadingPhotoServices}]"
                   :disabled="isLoadingPhotoServices"
                   @click="toggleExpanded">
            <div v-if="isLoadingPhotoServices" class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </CButton>
        </div>
      </div>
    </CCardHeader>

    <div class="d-flex flex-column flex-md-row secondary-header-industry justify-content-between">
      <template v-if="isIndustryER">
        <div class="mx-1">
          <translate>Avg purchase</translate>
          <span class="font-weight-semi-bold mx-2">
            {{ getAverage(industry.total_amount_eur, industry.order_count) | currency(currencyCodes.EUR) }}
          </span>
        </div>
        <div class="mx-1">
          <translate>Remarketing</translate>
          ({{ getPercentage(industry.total_amount_eur, industry.order_from_remarketing_amount_eur) | number }}%)
          <span class="font-weight-semi-bold mx-2">
            {{ industry.order_from_remarketing_amount_eur | currency(currencyCodes.EUR) }}
          </span>
        </div>
        <div class="mx-1" v-if="industry.industry === industriesKeys.RESORT">
          <translate>During</translate>
          ({{ getPercentage(industry.total_amount_eur, industry.order_during_amount_eur) | number }}%)
          <span class="font-weight-semi-bold mx-2">
            {{ industry.order_during_amount_eur | currency(currencyCodes.EUR) }}
          </span>
        </div>
        <div class="mx-1" v-if="industry.industry === industriesKeys.RESORT">
          <translate>After</translate>
          ({{ getPercentage(industry.total_amount_eur, (industry.total_amount_eur - industry.order_during_amount_eur)) | number }}%)
          <span class="font-weight-semi-bold mx-2">
            {{ (industry.total_amount_eur - industry.order_during_amount_eur) | currency(currencyCodes.EUR) }}
          </span>
        </div>
      </template>
      <template v-else>
        <div class="mx-1">
          <translate>Checkins</translate>
          <span class="font-weight-semi-bold mx-2">{{ industry.checkin_count | number }}</span>
        </div>
        <div class="mx-1">
          <translate>Remarketing</translate>
          ({{ getPercentage(industry.total_amount_eur, industry.order_from_remarketing_amount_eur) | number }}%)
          <span class="font-weight-semi-bold mx-2">
            {{ industry.order_from_remarketing_amount_eur | currency(currencyCodes.EUR) }}
          </span>
        </div>
        <div class="mr-3">
          <translate>Avg photos per guest</translate>
          <span class="font-weight-semi-bold mx-2">
            {{ (industry.order_medias_count / industry.checkin_count || 0) | number }}
          </span>
        </div>
        <div class="mr-3">
          <translate>Avg downloads</translate>
          <span class="font-weight-semi-bold mx-2">
            {{ (industry.download_count / industry.checkin_count || 0) | number }}
          </span>
        </div>
        <div class="">
          <translate>Avg shares</translate>
          <span class="font-weight-semi-bold mx-2">
            {{ (((industry.share_total_count - industry.download_count) / industry.checkin_count)) || 0 | number }}%
          </span>
        </div>
      </template>
    </div>

    <CCollapse :show="Boolean(isExpanded)" :duration="300">
      <div style="min-height: 100px">
        <CCardBody>
          <ViewLoadingMessage :loading="isLoadingPhotoServices || !photoServicesData"/>
          
          <template v-if="!isLoadingPhotoServices && photoServicesData && photoServicesData.results">
            <PhotoServiceItem
              v-for="(photoService, index) in photoServicesData.results"
              :key="index"
              :photo-service="photoService"
              :industry="industry.industry"
              :loading-events="loadingEvents[photoService.id] || false"
              @toggle-details="togglePhotographerDetails"
              @events-loaded="onEventsLoaded"
            />
          </template>
            
          <template v-if="!isLoadingPhotoServices && photoServicesData && photoServicesData.results && photoServicesData.results.length === 0">
            <div class="text-center"><h6 v-translate>No data</h6></div>
          </template>
        </CCardBody>
      </div>
    </CCollapse>
  </CCard>
</template>

<script>
import ViewLoadingMessage from "@/components/ViewLoadingMessage.vue"
import { mapGetters, mapActions } from "vuex"
import { currencyCodes } from "@/constants"
import { 
  INDUSTRIES_ER, 
  industriesConfig, 
  industriesKeys 
} from "@/views_pica/stats/constants"
import { getAverage, getPercentage } from "@/utils/functions"
import PhotoServiceItem from "./PhotoServiceItem.vue"

export default {
  name: "IndustryCard",
  
  components: { 
    ViewLoadingMessage,
    PhotoServiceItem 
  },
  
  props: {
    industry: {
      type: Object,
      required: true
    },
    autoExpand: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      isExpanded: false,
      photoServicesData: null,
      isLoadingPhotoServices: false,
      loadingEvents: {} // Track loading state per photo service ID
    }
  },
  
  computed: {
    ...mapGetters('salesReport', ['photoServicesByIndustry']),
    
    currencyCodes() {
      return currencyCodes
    },
    
    industriesKeys() {
      return industriesKeys
    },
    
    industriesConfig() {
      return industriesConfig
    },
    
    INDUSTRIES_ER() {
      return INDUSTRIES_ER
    },
    
    getIndustryConfig() {
      return this.industriesConfig[this.industry.industry]
    },
    
    isIndustryER() {
      return this.INDUSTRIES_ER.includes(this.industry.industry)
    }
  },

  async mounted() {
    if (this.autoExpand) {
      await this.loadPhotoServicesData()
      this.isExpanded = true
    }
  },

  watch: {
    async autoExpand(newVal) {
      if (newVal && !this.isExpanded) {
        await this.loadPhotoServicesData()
        this.isExpanded = true
      }
    }
  },

  methods: {
    ...mapActions('salesReport', ['fetchPhotoServicesByIndustry', 'fetchPhotoServiceEventsTotals']),
    
    getPercentage,
    getAverage,
    
    
    async toggleExpanded() {
      if (!this.isExpanded && !this.photoServicesData) {
        await this.loadPhotoServicesData()
      }
      
      this.isExpanded = !this.isExpanded
    },
    
    async loadPhotoServicesData() {
      this.isLoadingPhotoServices = true
      try {
        const data = await this.fetchPhotoServicesByIndustry(this.industry.industry)
        this.photoServicesData = data
      } catch (error) {
        console.error('Error loading photo services data:', error)
        this.photoServicesData = { results: [] }
      } finally {
        this.isLoadingPhotoServices = false
      }
    },
    
    async togglePhotographerDetails(photoService) {
      // Load events when opening photo service
      if (!photoService._toggled && !photoService.events) {
        this.$set(this.loadingEvents, photoService.id, true)
        try {
          await this.fetchPhotoServiceEventsTotals({
            photoServiceId: photoService.id,
            industry: this.industry.industry
          })
        } catch (error) {
          console.error('Error loading photo service events:', error)
        } finally {
          this.$set(this.loadingEvents, photoService.id, false)
        }
      }
      
      this.$set(photoService, "_toggled", !photoService._toggled)
      this.$set(photoService, "_viewed", true)
    },
    
    onEventsLoaded(eventData) {
      // Handle events loaded if needed
      console.log('Events loaded for photo service:', eventData.photoService.company_name)
    }
  }
}
</script>

<style scoped>
.btn-grid-toggle.loading::before {
  display: none;
}
</style>